/**
 * Placeholder Trace helpers.
 *
 * Pure functions that compute the producer/consumer trace subgraph for a
 * placeholder key on the workflow canvas. No React, no zustand — these are
 * the building blocks consumed by `usePlaceholderTraceStore` /
 * `useTracePlaceholder` and tested in isolation.
 */

const EXCLUDED_EDGE_TYPES = new Set(['splitConvergeLink', 'formRedirectLink']);

const PLACEHOLDER_REF_REGEX = /[$!]\{([^}]+)\}/g;

/**
 * Extract the set of placeholder keys referenced inside a component's
 * rawComponentData. Scans JSON.stringify output for `${key}` and `!{key}`
 * patterns. Type-agnostic: works for plain string fields and TipTap-rendered
 * HTML (which contains `${foo}` literal text alongside `data-placeholder-key`).
 *
 * @param {*} rawComponentData
 * @returns {Set<string>}
 */
export function extractReferencedKeys(rawComponentData) {
  const refs = new Set();
  if (rawComponentData == null) return refs;

  let serialized;
  try {
    serialized = JSON.stringify(rawComponentData);
  } catch {
    return refs;
  }
  if (!serialized) return refs;

  PLACEHOLDER_REF_REGEX.lastIndex = 0;
  let match;
  while ((match = PLACEHOLDER_REF_REGEX.exec(serialized)) !== null) {
    const raw = match[1];
    if (raw == null) continue;
    const key = raw.trim();
    if (key === '') continue;
    refs.add(key);
  }
  return refs;
}

/**
 * Invert the existing `outputsByComponent` map (produced by
 * `computeAllPlaceholdersMap`) into a key → producer index.
 *
 * @param {Object} outputsByComponent  shape: { componentId: [{key, type}, ...] }
 * @returns {Map<string, { ids: Set<string>, type: string }>}
 */
export function buildProducerIndex(outputsByComponent) {
  const index = new Map();
  if (!outputsByComponent) return index;

  for (const [componentId, outputs] of Object.entries(outputsByComponent)) {
    if (!Array.isArray(outputs)) continue;
    for (const output of outputs) {
      const key = output?.key;
      if (key == null) continue;
      const keyStr = String(key);
      let entry = index.get(keyStr);
      if (!entry) {
        entry = { ids: new Set(), type: output?.type ? String(output.type) : 'text' };
        index.set(keyStr, entry);
      }
      entry.ids.add(componentId);
    }
  }
  return index;
}

/**
 * Read consumer refs for a component, using object-identity caching.
 * The cache stores `{ dataRef, refs }` per componentId. When the data
 * identity changes (zustand replaces the object on edit) the cache misses
 * and recomputes that one component.
 *
 * @param {string} componentId
 * @param {*} rawComponentData
 * @param {Map<string, { dataRef: *, refs: Set<string> }>} cache
 * @returns {Set<string>}
 */
export function getConsumerRefs(componentId, rawComponentData, cache) {
  const cached = cache.get(componentId);
  if (cached && cached.dataRef === rawComponentData) {
    return cached.refs;
  }
  const refs = extractReferencedKeys(rawComponentData);
  cache.set(componentId, { dataRef: rawComponentData, refs });
  return refs;
}

function bfs(startIds, adjacency) {
  const visited = new Set();
  const queue = [];
  for (const id of startIds) {
    if (id != null && !visited.has(id)) {
      visited.add(id);
      queue.push(id);
    }
  }
  while (queue.length > 0) {
    const current = queue.shift();
    const neighbors = adjacency.get(current);
    if (!neighbors) continue;
    for (const next of neighbors) {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }
  return visited;
}

/**
 * Compute the trace subgraph for a placeholder key.
 *
 * @param {string} key
 * @param {{
 *   components: Array<{componentId: string, rawComponentData: *}>,
 *   edges: Array<{id: string, source: string, target: string, type?: string}>,
 *   outputsByComponent: Object,
 *   refsCache: Map
 * }} ctx
 * @returns {{
 *   activeKey: string,
 *   producerIds: Set<string>,
 *   consumerIds: Set<string>,
 *   traceNodeIds: Set<string>,
 *   traceEdgeIds: Set<string>,
 *   type: (string|null)
 * }}
 */
export function tracePlaceholder(key, ctx) {
  const empty = {
    activeKey: key,
    producerIds: new Set(),
    consumerIds: new Set(),
    traceNodeIds: new Set(),
    traceEdgeIds: new Set(),
    type: null,
  };
  if (!key || !ctx) return empty;

  const { components = [], edges = [], outputsByComponent = {}, refsCache } = ctx;
  const cache = refsCache instanceof Map ? refsCache : new Map();

  const producerIndex = buildProducerIndex(outputsByComponent);
  const producerEntry = producerIndex.get(key);
  const producerIds = producerEntry ? new Set(producerEntry.ids) : new Set();
  const type = producerEntry ? producerEntry.type : null;

  const consumerIds = new Set();
  for (const comp of components) {
    const id = comp?.componentId;
    if (!id) continue;
    const refs = getConsumerRefs(id, comp.rawComponentData, cache);
    if (refs.has(key)) consumerIds.add(id);
  }

  const forwardAdj = new Map();
  const reverseAdj = new Map();
  for (const edge of edges) {
    if (!edge || EXCLUDED_EDGE_TYPES.has(edge.type)) continue;
    const { source, target } = edge;
    if (!source || !target) continue;
    if (!forwardAdj.has(source)) forwardAdj.set(source, new Set());
    forwardAdj.get(source).add(target);
    if (!reverseAdj.has(target)) reverseAdj.set(target, new Set());
    reverseAdj.get(target).add(source);
  }

  const forwardReachable = bfs(producerIds, forwardAdj);
  const backwardReachable = bfs(consumerIds, reverseAdj);

  const traceNodeIds = new Set();
  for (const id of producerIds) traceNodeIds.add(id);
  for (const id of consumerIds) traceNodeIds.add(id);
  for (const id of forwardReachable) {
    if (backwardReachable.has(id)) traceNodeIds.add(id);
  }

  const traceEdgeIds = new Set();
  for (const edge of edges) {
    if (!edge || EXCLUDED_EDGE_TYPES.has(edge.type)) continue;
    if (!edge.id) continue;
    if (traceNodeIds.has(edge.source) && traceNodeIds.has(edge.target)) {
      traceEdgeIds.add(edge.id);
    }
  }

  return { activeKey: key, producerIds, consumerIds, traceNodeIds, traceEdgeIds, type };
}
